import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { FilterOptions } from '@/types';
import { driverService, type Driver, type DriverFilters, type DriverStats, type CreateDriverData, type UpdateDriverData } from '@/services/driverService';

interface DriversState {
  drivers: Driver[];
  selectedDriver: Driver | null;
  filters: DriverFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: DriverStats | null;
  loading: boolean;
  error: string | null;
}

// Async thunks for API operations
export const fetchDrivers = createAsyncThunk(
  'drivers/fetchDrivers',
  async ({ filters, page, limit }: { filters?: DriverFilters; page?: number; limit?: number }) => {
    const response = await driverService.getDrivers(filters || {}, page || 1, limit || 20);
    return {
      drivers: response.drivers.map(driver => driverService.convertToDriver(driver)),
      pagination: response.pagination,
      stats: response.stats,
    };
  }
);

export const fetchDriver = createAsyncThunk(
  'drivers/fetchDriver',
  async (id: string) => {
    const driver = await driverService.getDriver(id);
    return driverService.convertToDriver(driver);
  }
);

export const createDriver = createAsyncThunk(
  'drivers/createDriver',
  async (driverData: CreateDriverData) => {
    const driver = await driverService.createDriver(driverData);
    return driverService.convertToDriver(driver);
  }
);

export const updateDriver = createAsyncThunk(
  'drivers/updateDriver',
  async ({ id, data }: { id: string; data: UpdateDriverData }) => {
    const driver = await driverService.updateDriver(id, data);
    return driverService.convertToDriver(driver);
  }
);

export const deleteDriver = createAsyncThunk(
  'drivers/deleteDriver',
  async (id: string) => {
    await driverService.deleteDriver(id);
    return id;
  }
);

export const approveDriver = createAsyncThunk(
  'drivers/approveDriver',
  async ({ id, note }: { id: string; note?: string }) => {
    const driver = await driverService.approveDriver(id, note);
    return driverService.convertToDriver(driver);
  }
);

export const rejectDriver = createAsyncThunk(
  'drivers/rejectDriver',
  async ({ id, reason }: { id: string; reason: string }) => {
    const driver = await driverService.rejectDriver(id, reason);
    return driverService.convertToDriver(driver);
  }
);

export const suspendDriver = createAsyncThunk(
  'drivers/suspendDriver',
  async ({ id, reason, duration }: { id: string; reason: string; duration?: number }) => {
    const driver = await driverService.suspendDriver(id, reason, duration);
    return driverService.convertToDriver(driver);
  }
);

export const updateDriverStatus = createAsyncThunk(
  'drivers/updateDriverStatus',
  async ({ id, status }: { id: string; status: 'online' | 'offline' | 'busy' }) => {
    const driver = await driverService.updateDriverStatus(id, status);
    return driverService.convertToDriver(driver);
  }
);

const initialState: DriversState = {
  drivers: [],
  selectedDriver: null,
  filters: {
    search: '',
    status: [],
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  stats: null,
  loading: false,
  error: null,
};

const driversSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    setDrivers: (state, action: PayloadAction<Driver[]>) => {
      state.drivers = action.payload;
    },
    addDriver: (state, action: PayloadAction<Driver>) => {
      state.drivers.unshift(action.payload);
    },
    updateDriverLocal: (state, action: PayloadAction<Driver>) => {
      const index = state.drivers.findIndex(d => d.id === action.payload.id);
      if (index !== -1) {
        state.drivers[index] = action.payload;
      }
      if (state.selectedDriver?.id === action.payload.id) {
        state.selectedDriver = action.payload;
      }
    },
    removeDriver: (state, action: PayloadAction<string>) => {
      state.drivers = state.drivers.filter(d => d.id !== action.payload);
      if (state.selectedDriver?.id === action.payload) {
        state.selectedDriver = null;
      }
    },
    setSelectedDriver: (state, action: PayloadAction<Driver | null>) => {
      state.selectedDriver = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<DriverFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setStats: (state, action: PayloadAction<DriverStats>) => {
      state.stats = action.payload;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch drivers
    builder
      .addCase(fetchDrivers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.loading = false;
        state.drivers = action.payload.drivers;
        state.pagination = action.payload.pagination;
        state.stats = action.payload.stats;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch drivers';
      });

    // Fetch single driver
    builder
      .addCase(fetchDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDriver = action.payload;
      })
      .addCase(fetchDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch driver';
      });

    // Create driver
    builder
      .addCase(createDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.drivers.unshift(action.payload);
      })
      .addCase(createDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create driver';
      });

    // Update driver
    builder
      .addCase(updateDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.drivers.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.selectedDriver?.id === action.payload.id) {
          state.selectedDriver = action.payload;
        }
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update driver';
      });

    // Delete driver
    builder
      .addCase(deleteDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.drivers = state.drivers.filter(d => d.id !== action.payload);
        if (state.selectedDriver?.id === action.payload) {
          state.selectedDriver = null;
        }
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete driver';
      });

    // Approve driver
    builder
      .addCase(approveDriver.fulfilled, (state, action) => {
        const index = state.drivers.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.selectedDriver?.id === action.payload.id) {
          state.selectedDriver = action.payload;
        }
      });

    // Reject driver
    builder
      .addCase(rejectDriver.fulfilled, (state, action) => {
        const index = state.drivers.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.selectedDriver?.id === action.payload.id) {
          state.selectedDriver = action.payload;
        }
      });

    // Suspend driver
    builder
      .addCase(suspendDriver.fulfilled, (state, action) => {
        const index = state.drivers.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.selectedDriver?.id === action.payload.id) {
          state.selectedDriver = action.payload;
        }
      });

    // Update driver status
    builder
      .addCase(updateDriverStatus.fulfilled, (state, action) => {
        const index = state.drivers.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.selectedDriver?.id === action.payload.id) {
          state.selectedDriver = action.payload;
        }
      });
  },
});

export const {
  setDrivers,
  addDriver,
  updateDriverLocal,
  removeDriver,
  setSelectedDriver,
  setFilters,
  setStats,
  clearFilters,
  setPagination,
  setLoading,
  setError,
} = driversSlice.actions;

export default driversSlice.reducer;

// Selectors
export const selectDrivers = (state: { drivers: DriversState }) => state.drivers;
export const selectDriversList = (state: { drivers: DriversState }) => state.drivers.drivers;
export const selectSelectedDriver = (state: { drivers: DriversState }) => state.drivers.selectedDriver;
export const selectDriversLoading = (state: { drivers: DriversState }) => state.drivers.loading;
export const selectDriversError = (state: { drivers: DriversState }) => state.drivers.error;
export const selectDriversStats = (state: { drivers: DriversState }) => state.drivers.stats;
export const selectDriversPagination = (state: { drivers: DriversState }) => state.drivers.pagination;
export const selectDriversFilters = (state: { drivers: DriversState }) => state.drivers.filters;